/**
 * Thermal Viewer Component
 * Renders thermal orthomosaics with temperature analysis
 */

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../../../store/AppContext';
import { useFilePicker, FILE_ACCEPTS } from '../../../hooks/useFilePicker';
import { api } from '../../../services/api';
import { EmptyState } from '../../../components/common/EmptyState';
import '../../../components/viewers/ViewerCommon.css';
import './ThermalViewer.css';

interface ThermalCalibration {
  slope: number;
  offset: number;
  unit: 'celsius' | 'fahrenheit';
}

const DEFAULT_CALIBRATION: ThermalCalibration = {
  slope: 0.04,
  offset: -273.15,
  unit: 'celsius',
};

const COLOR_PALETTES = {
  ironbow: [
    [0, 0, 0],
    [0, 0, 128],
    [128, 0, 128],
    [255, 0, 0],
    [255, 128, 0],
    [255, 255, 0],
    [255, 255, 255],
  ],
  rainbow: [
    [0, 0, 255],
    [0, 255, 255],
    [0, 255, 0],
    [255, 255, 0],
    [255, 0, 0],
  ],
  grayscale: [
    [0, 0, 0],
    [255, 255, 255],
  ],
};

export function ThermalViewer() {
  const { state, updateProject, setStatus } = useApp();
  const isPortal = useLocation().pathname.startsWith('/portal');
  const { pickFiles } = useFilePicker();
  const [isLoading, setIsLoading] = useState(false);
  const [calibration, setCalibration] = useState<ThermalCalibration>(DEFAULT_CALIBRATION);
  const [palette, setPalette] = useState<keyof typeof COLOR_PALETTES>('ironbow');
  const [cursorTemp, setCursorTemp] = useState<number | null>(null);
  const [showCalibration, setShowCalibration] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  const project = state.currentProject;
  const thermalPath = project?.geotiff?.thermalPath;
  const savedCalibration = project?.geotiff?.thermalCalibration;

  // Load saved calibration
  useEffect(() => {
    if (savedCalibration) {
      setCalibration(savedCalibration);
    }
  }, [savedCalibration]);

  // Initialize viewer
  useEffect(() => {
    if (!thermalPath || !containerRef.current) return;

    const initViewer = async () => {
      setIsLoading(true);
      setStatus('Loading thermal data...');

      try {
        const maplibregl = await import('maplibre-gl');
        // @ts-expect-error CSS import handled by bundler
        await import('maplibre-gl/dist/maplibre-gl.css');

        const container = containerRef.current!;

        const map = new maplibregl.default.Map({
          container,
          style: {
            version: 8,
            sources: {},
            layers: [
              {
                id: 'background',
                type: 'background',
                paint: { 'background-color': '#1a1a1a' }
              }
            ],
            glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf'
          },
          center: [0, 0],
          zoom: 2,
        });

        mapRef.current = map;

        map.on('load', () => {
          // In production, load the thermal GeoTIFF and apply color mapping
          setStatus('Thermal data loaded');
          setIsLoading(false);
        });

        // Track temperature at cursor
        map.on('mousemove', (_e: any) => {
          // In production, sample the raster value at cursor position
          // For demo, show random temp in expected range
          const demoTemp = 20 + Math.random() * 30;
          setCursorTemp(demoTemp);
        });

        map.on('mouseout', () => {
          setCursorTemp(null);
        });

        map.addControl(new maplibregl.default.NavigationControl(), 'top-right');

        return () => {
          map.remove();
        };
      } catch (error) {
        console.error('Failed to load thermal viewer:', error);
        setStatus('Failed to load thermal data');
        setIsLoading(false);
      }
    };

    const cleanup = initViewer();
    return () => {
      cleanup?.then(fn => fn?.());
      mapRef.current = null;
    };
  }, [thermalPath, setStatus]);

  const handleAddThermal = async () => {
    const result = await pickFiles({
      accept: FILE_ACCEPTS.GEOTIFF,
    });

    if (result.canceled || !result.files[0]) return;

    const file = result.files[0];

    // Upload the file to the server
    const uploaded = await api.file.upload(file);

    if (project) {
      await updateProject(project.id, {
        geotiff: {
          ...project.geotiff,
          thermalPath: uploaded.storagePath,
        },
      });
    }
  };

  const handleSaveCalibration = async () => {
    if (!project) return;

    await updateProject(project.id, {
      geotiff: {
        ...project.geotiff,
        thermalCalibration: calibration,
      },
    });

    setShowCalibration(false);
    setStatus('Calibration saved');
  };

  const handleClear = async () => {
    if (!project) return;
    await updateProject(project.id, {
      geotiff: {
        ...project.geotiff,
        thermalPath: undefined,
        thermalCalibration: undefined,
      },
    });
    setStatus('Thermal data cleared');
  };

  const formatTemp = (temp: number): string => {
    if (calibration.unit === 'fahrenheit') {
      return `${(temp * 9/5 + 32).toFixed(1)}F`;
    }
    return `${temp.toFixed(1)}C`;
  };

  if (!thermalPath) {
    return (
      <div className="viewer">
        <EmptyState
          icon="image"
          title="No Thermal Data"
          description="Add a thermal orthomosaic to analyze temperature patterns"
          action={{ label: 'Add Thermal Image', onClick: handleAddThermal }}
        />
      </div>
    );
  }

  return (
    <div className="viewer">
      {!isPortal && (
        <div className="viewer__toolbar">
          <span className="viewer__toolbar-title">Thermal Analysis</span>
          <div className="viewer__toolbar-actions">
            <select
              className="viewer__toolbar-select"
              value={palette}
              onChange={e => setPalette(e.target.value as keyof typeof COLOR_PALETTES)}
            >
              <option value="ironbow">Ironbow</option>
              <option value="rainbow">Rainbow</option>
              <option value="grayscale">Grayscale</option>
            </select>
            <button
              className="viewer__toolbar-btn"
              onClick={() => setShowCalibration(!showCalibration)}
            >
              Calibration
            </button>
            <button className="viewer__toolbar-btn" onClick={handleClear}>
              Clear
            </button>
          </div>
        </div>
      )}


      <div className="viewer__content" ref={containerRef}>
        {isLoading && (
          <div className="viewer__loading">
            <div className="viewer__loading-spinner" />
            <span className="viewer__loading-text">Loading thermal data...</span>
          </div>
        )}

        {/* Temperature Display */}
        {cursorTemp !== null && (
          <div className="thermal-temp">
            <span className="thermal-temp__label">Temperature:</span>
            <span className="thermal-temp__value">{formatTemp(cursorTemp)}</span>
          </div>
        )}

        {/* Color Scale Legend */}
        <div className="thermal-legend">
          <div
            className="thermal-legend__gradient"
            style={{
              background: `linear-gradient(to top, ${COLOR_PALETTES[palette]
                .map(c => `rgb(${c.join(',')})`)
                .join(', ')})`
            }}
          />
          <div className="thermal-legend__labels">
            <span>Hot</span>
            <span>Cold</span>
          </div>
        </div>

        {/* Calibration Panel */}
        {showCalibration && (
          <div className="thermal-calibration">
            <h4 className="thermal-calibration__title">Temperature Calibration</h4>
            <div className="thermal-calibration__form">
              <div className="thermal-calibration__field">
                <label>Slope</label>
                <input
                  type="number"
                  step="0.001"
                  value={calibration.slope}
                  onChange={e => setCalibration(c => ({ ...c, slope: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="thermal-calibration__field">
                <label>Offset</label>
                <input
                  type="number"
                  step="0.1"
                  value={calibration.offset}
                  onChange={e => setCalibration(c => ({ ...c, offset: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="thermal-calibration__field">
                <label>Unit</label>
                <select
                  value={calibration.unit}
                  onChange={e => setCalibration(c => ({ ...c, unit: e.target.value as 'celsius' | 'fahrenheit' }))}
                >
                  <option value="celsius">Celsius</option>
                  <option value="fahrenheit">Fahrenheit</option>
                </select>
              </div>
            </div>
            <div className="thermal-calibration__actions">
              <button className="button" onClick={() => setShowCalibration(false)}>
                Cancel
              </button>
              <button className="button button--primary" onClick={handleSaveCalibration}>
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
